import CachedIcon from '@mui/icons-material/Cached'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { enqueueSnackbar } from 'notistack'
import { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { BackContinue } from '@components/back_continue/back_continue'
import { useBookingContext } from '@providers/booking/booking_context'
import { useSessionContext } from '@providers/session/session_context'
import { MAX_WIDGET_SIZE, STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { buildErrorMessage } from '@shared/error'
import { i18WithParams as t } from '@shared/locale'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import {
  AdyenCardPayment,
  submitPaymentForm,
  TOnPaymentCompleted,
} from 'components-ui/src/adyen_card_payment/adyen_card_payment'
import { FlexCardDisplay } from 'components-ui/src/flex_card_display/flex_card_display'
import { getPoliciesData } from 'components-ui/src/purchase_summary/purchase_receipt/helper'
import { PurchaseReceipt } from 'components-ui/src/purchase_summary/purchase_receipt/purchase_receipt'
import { PurchaseReceiptSkeleton } from 'components-ui/src/purchase_summary/purchase_receipt/purchase_receipt_skeleton'
import { SurchargePolicy } from 'components-ui/src/purchase_summary/surcharge_policy/surcharge_policy'
import { formatNumberAsPrice } from 'components-ui/src/shared/common'
import { allowCardAndPrepaidPayments, allowNoPayments } from 'components-ui/src/shared/location'
import { isVisitOrder } from 'components-ui/src/shared/orders'
import { Spacer, Spacer2, Spacer3 } from 'components-ui/src/spacer/spacer'
import { TextInputNumber } from 'components-ui/src/textinput-number/textinput_number'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { useVouchersBalance } from 'hooks/use_vouchers_balance'
import { useClientBookingConfigs } from 'service-api/src/hooks/use_client_booking_configs'
import { useClientReceipt } from 'service-api/src/hooks/use_client_receipt'
import { ClientBookingPaymentService } from 'service-api/src/services'
import { isHttpGone } from 'service-api/src/shared/request'
import { getBillingAddress, isPaymentInitialized, paymentToConfirmOrCancel, removeDecimal } from './helper'
import { usePayment } from './use_payment'
import { adyenPaymentToUseFromPendingOrder, getDepositDue } from 'components-ui/src/shared/payment'

let promiseResolve: (value: boolean | PromiseLike<boolean>) => void, promiseReject: (err: unknown) => void

export const AdyenPayment = () => {
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const { navigateRoute } = useRoutes()
  const renderStyle = useMediaQuery(theme.breakpoints.down('md')) ? 'mobile' : 'desktop'
  const isMobile = renderStyle === 'mobile'
  const receipt = useClientReceipt(locationName, bookingCtx.state.data.reservation?.order?.id || '', 'PAYMENT')
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
  const isGrooming = bookingCtx.state.serviceTypeName === 'grooming'
  const order = bookingCtx.state.data.reservation?.order
  const isVisit = isVisitOrder(order)
  const bookingConfig = useClientBookingConfigs(locationName, order?.primaryServiceType?.serviceType?.name || '', {
    enabled: isVisit && !isGrooming,
  })
  const { walletBalance, fetchingWalletBalance } = useVouchersBalance(sessionCtx.state.user?.lupId || '', {
    options: { fetchWalletBalance: true, fetchUnusedVouchersBalance: false },
  })

  useRedirectNoData()
  useEffect(() => {
    if (bookingCtx.state.currentState !== 'payment') {
      bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'payment' } })
    }
  })

  const locationDisplayName = bookingCtx.state.data.reservation?.order?.location?.displayName ?? locationName ?? ''
  const allowCardAndPrepaid = allowCardAndPrepaidPayments(bookingCtx.state.data.reservation?.locationBookingConf)
  const noPaymentsAllowed = allowNoPayments(bookingCtx.state.data.reservation?.locationBookingConf)

  const isCardPayment = !noPaymentsAllowed && bookingCtx.state.cart.payment.method === 'payNow'
  const isPrepaid = !noPaymentsAllowed && bookingCtx.state.cart.payment.method === 'prePaid'
  const payingAtFacility = noPaymentsAllowed || isPrepaid
  const depositDue = getDepositDue(bookingCtx.state.data.reservation?.order)

  const [walletAmountInput, setWalletAmountInput] = useState<string | null>(null)
  const [appliedWalletAmount, setAppliedWalletAmount] = useState<number>(0)
  const [payWithCardEnabled, setPayWithCardEnabled] = useState<boolean>(false)
  const [walletError, setWalletError] = useState<string | null>(null)
  const [isCancellingPayment, setIsCancellingPayment] = useState<boolean>(false)

  const isWalletFlowActive = isCardPayment && walletBalance > 0 && depositDue > 0
  const walletCoversDeposit = isWalletFlowActive && appliedWalletAmount >= depositDue
  const maxApplicableWalletAmount = Math.min(depositDue, walletBalance)
  const isPayWithCardDisabled = walletCoversDeposit
  const walletApplied = appliedWalletAmount > 0

  // When there is definitively no wallet balance, automatically enable card payment and initialize the form
  useEffect(() => {
    if (!fetchingWalletBalance && walletBalance === 0 && isCardPayment && !payWithCardEnabled) {
      setPayWithCardEnabled(true)
    }
  }, [walletBalance, fetchingWalletBalance, isCardPayment, payWithCardEnabled])

  const { isLoading: isInitializingPayment } = usePayment(payWithCardEnabled, appliedWalletAmount)
  const isPaymentFormLoading = isInitializingPayment || isCancellingPayment
  const walletControlsDisabled = payWithCardEnabled || isPaymentFormLoading

  const resetWalletAndCard = () => {
    if (walletApplied) {
      enqueueSnackbar(t('paymentForm.walletFundsRemoved'), { variant: 'error' })
      setAppliedWalletAmount(0)
    }
    setWalletAmountInput('')
    setPayWithCardEnabled(false)
  }

  const hideRefundPolicy = !isVisit || noPaymentsAllowed
  const policyCardsData = useMemo(
    () => (receipt.data ? getPoliciesData(receipt.data, hideRefundPolicy) : []),
    [receipt.data, hideRefundPolicy]
  )

  const handleWalletInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setWalletAmountInput(value)

    const rawValue = Number(value)
    if (value === '' || Number.isNaN(rawValue) || rawValue <= 0) {
      setWalletError(null)
      return
    }

    if (rawValue > depositDue) {
      setWalletError(t('paymentForm.walletError.exceedsDueToday'))
      return
    }

    if (rawValue > walletBalance) {
      setWalletError(t('paymentForm.walletError.exceedsBalance'))
      return
    }

    setWalletError(null)
  }

  const handleApplyWalletAmount = () => {
    if (!isWalletFlowActive || walletError) {
      return
    }

    const rawValue = Number(walletAmountInput)
    if (Number.isNaN(rawValue) || rawValue <= 0) {
      return
    }

    const applied = Math.min(rawValue, maxApplicableWalletAmount)
    setAppliedWalletAmount(applied)
    setWalletAmountInput(applied.toFixed(2))

    enqueueSnackbar(t('paymentForm.walletFundsApplied'), { variant: 'success' })

    // If there is still deposit due left after applying wallet funds, auto-enable card payment
    if (applied < depositDue) {
      setPayWithCardEnabled(true)
    }
  }

  const handleRemoveWalletAmount = () => {
    enqueueSnackbar(t('paymentForm.walletFundsRemoved'), { variant: 'error' })
    setAppliedWalletAmount(0)
    setWalletAmountInput('')
    setWalletError(null)
  }

  const handleTogglePayWithCard = (event: ChangeEvent<HTMLInputElement>) => {
    if (isPayWithCardDisabled || isPaymentFormLoading) {
      return
    }
    setPayWithCardEnabled(event.target.checked)
  }

  const handleResetPayment = async () => {
    const { order } = bookingCtx.state.data.reservation || {}
    const paymentToCancel = paymentToConfirmOrCancel(order)

    // if no payFacType, then payment has not been initialized so it doesn't need to be canceled
    if (!order?.id || !paymentToCancel?.id || !isPaymentInitialized(paymentToCancel)) {
      resetWalletAndCard()
      return
    }

    try {
      setIsCancellingPayment(true)
      const reservationResponse = await ClientBookingPaymentService.cancelPayment(
        locationName,
        order.id,
        paymentToCancel.id
      )
      bookingCtx.dispatch({ type: 'SET_RESERVATION_DATA', payload: { value: reservationResponse } })
      resetWalletAndCard()
    } catch (error) {
      enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
      captureError(error as Error)
    } finally {
      setIsCancellingPayment(false)
    }
  }

  const payAtFacility = async () => {
    const { order } = bookingCtx.state.data.reservation || {}
    if (!order?.id) {
      return false
    }
    if (!isPaymentInitialized(paymentToConfirmOrCancel(order) || {})) {
      await ClientBookingPaymentService.initializePayment(locationName, order.id)
    }
    const reservationResponse = await ClientBookingPaymentService.offlinePayment(locationName, order.id)
    bookingCtx.dispatch({
      type: 'SET_FIRE_PURCHASE_EVENT',
      payload: { value: true },
    })
    navigateRoute('paymentSummary', { orderId: reservationResponse.order?.id || '' })
    return true
  }

  const adyenPay = () => {
    const promise = new Promise<boolean>((resolve, reject) => {
      promiseResolve = resolve
      promiseReject = reject
    })

    // this is only initiating the payment, the function is synchronous
    // the actual payment is done in the AdyenCardPayment component and
    // the result is passed to the onPaymentCompleted callback or onError
    // on those callbacks the promise will be resolved or rejected
    submitPaymentForm('paymentFormId')
    return promise
  }

  const onClickContinue = async () => {
    try {
      if (walletCoversDeposit) {
        const orderId = bookingCtx.state.data.reservation?.order?.id || ''
        await ClientBookingPaymentService.initializePayment(locationName, orderId, appliedWalletAmount)
        const reservationResponse = await ClientBookingPaymentService.voucherCashPayment(locationName, orderId)
        bookingCtx.dispatch({
          type: 'SET_FIRE_PURCHASE_EVENT',
          payload: { value: true },
        })
        navigateRoute('paymentSummary', { orderId: reservationResponse.order?.id || '' })
        return true
      }

      const rc = await (payingAtFacility ? payAtFacility() : adyenPay())
      return typeof rc !== 'undefined'
    } catch (err) {
      if (isHttpGone(err)) {
        navigateRoute('detailsPolicies', {})
        enqueueSnackbar(t('paymentForm.orderExpired'), { variant: 'info' })
      } else {
        enqueueSnackbar(buildErrorMessage(err), { variant: 'error' })
      }
      captureError(err as Error)
      return false
    }
  }

  const onChangePaymentMethod = (ev: ChangeEvent<HTMLInputElement>) => {
    const { value } = ev.target
    if (value === 'prePaid') {
      handleResetPayment()
    }
    bookingCtx.dispatch({ type: 'SET_PAYMENT_METHOD', payload: { value: value } })
  }

  const onPaymentCompleted = async (result: TOnPaymentCompleted) => {
    if (result.failed) {
      setErrorMsg(t('payments.error.general'))
      // close the promise created on adyenPay function
      promiseResolve(false)
      return
    }

    try {
      const reservationResponse = await ClientBookingPaymentService.adyenPayment(
        locationName,
        bookingCtx.state.data.reservation?.order?.id || '',
        result.sessionResult
      )
      enqueueSnackbar(t('payments.success'), { variant: 'success' })
      bookingCtx.dispatch({
        type: 'SET_FIRE_PURCHASE_EVENT',
        payload: { value: true },
      })
      navigateRoute('paymentSummary', { orderId: reservationResponse.order?.id || '' })
    } catch (e) {
      captureError(e as Error)
      setErrorMsg(t('payments.error.general'))
    }

    // close the promise created on adyenPay function
    promiseResolve(false)
  }

  const onPaymentError = (err: Error) => {
    if (err.message === 'invalid-form') {
      // this type of error is handled by the form component
      // so we don't need to show it here
      promiseResolve(false)
    } else {
      captureError(err)
      // close the promise created on adyenPay function
      promiseReject(err)
    }
  }

  const continueLabel = isGrooming
    ? t('paymentForm.requestNow')
    : isVisit
      ? t('paymentForm.bookNow')
      : t('paymentForm.purchaseNow')

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Typography variant="h2">{t('paymentForm.title')}</Typography>
      <Spacer2 />

      <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={3} flex={1}>
        <Stack direction="column" spacing={3} flex={1} width="100%">
          {allowCardAndPrepaid && (
            <Paper elevation={1} sx={{ p: 2, pb: 3, borderRadius: `${isMobile ? 0 : theme.shape.borderRadius}px` }}>
              <Typography variant="h5">{t('paymentForm.options')}</Typography>
              <Spacer3 />
              <RadioGroup value={bookingCtx.state.cart.payment.method} onChange={onChangePaymentMethod}>
                <FormControlLabel
                  value="payNow"
                  control={<Radio disabled={isPaymentFormLoading} />}
                  label={t('paymentForm.payNow')}
                />
                <FormControlLabel
                  value="prePaid"
                  control={<Radio disabled={isPaymentFormLoading} />}
                  label={t('paymentForm.prePaid')}
                />
              </RadioGroup>
            </Paper>
          )}

          {noPaymentsAllowed && (
            <Paper elevation={1} sx={{ p: 2, pb: 3, borderRadius: `${isMobile ? 0 : theme.shape.borderRadius}px` }}>
              <Typography variant="h5">{t('paymentForm.options')}</Typography>
              <Spacer3 />
              <Typography variant="body1">{t('paymentForm.dueAtFacility', { locationDisplayName })}</Typography>
            </Paper>
          )}

          {isCardPayment && (
            <Paper elevation={1} sx={{ p: 2, pb: 3, borderRadius: `${isMobile ? 0 : theme.shape.borderRadius}px` }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h5">{t('paymentForm.method')}</Typography>
                {isWalletFlowActive && payWithCardEnabled && !isPaymentFormLoading && (
                  <Box>
                    <Button variant="outlined" size="small" startIcon={<CachedIcon />} onClick={handleResetPayment}>
                      {t('global.label.reset')}
                    </Button>
                  </Box>
                )}
              </Stack>
              {isWalletFlowActive && (
                <>
                  <Spacer3 />
                  {walletError && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {walletError}
                    </Alert>
                  )}
                  <Box
                    sx={{
                      backgroundColor: theme.palette.success.shade8,
                      borderRadius: 1,
                      px: 2,
                      py: 2,
                    }}
                  >
                    <Stack direction="row" alignItems="center">
                      <Typography variant="body1bold">{t('paymentForm.applyWalletFunds')}</Typography>
                      <Typography variant="body1bold" sx={{ ml: 1, mr: 0.5 }}>
                        $
                      </Typography>
                      <TextInputNumber
                        size="small"
                        autoFocus={true}
                        decimalScale={2}
                        value={!walletApplied && walletControlsDisabled ? '0.00' : (walletAmountInput ?? '')}
                        onChange={handleWalletInputChange}
                        disabled={walletApplied || walletControlsDisabled}
                        error={!!walletError}
                        sx={{ width: 120, mb: 0, backgroundColor: theme.palette.common.white }}
                      />
                      {walletApplied ? (
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          onClick={handleRemoveWalletAmount}
                          disabled={walletControlsDisabled}
                          sx={{ ml: 0.5 }}
                        >
                          {t('global.label.remove')}
                        </Button>
                      ) : (
                        <Button
                          onClick={handleApplyWalletAmount}
                          disabled={walletControlsDisabled || !!walletError}
                          sx={{ ml: 0.5 }}
                          variant="contained"
                          color="success"
                        >
                          {t('global.label.apply')}
                        </Button>
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {`(${t('paymentForm.walletBalanceAvailable', { amount: formatNumberAsPrice(walletBalance) })})`}
                    </Typography>
                  </Box>
                  <Spacer height={1} />
                  <Box>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={payWithCardEnabled}
                          onChange={handleTogglePayWithCard}
                          disabled={isPayWithCardDisabled}
                        />
                      }
                      label={
                        walletApplied ? (
                          <Typography
                            variant="body1"
                            dangerouslySetInnerHTML={{
                              __html: t('paymentForm.payRemainingAmountWithCard', {
                                amount: formatNumberAsPrice(depositDue - appliedWalletAmount),
                              }),
                            }}
                          />
                        ) : (
                          t('paymentForm.payWithCard')
                        )
                      }
                    />
                  </Box>
                </>
              )}
              {!!bookingCtx.state.data.reservation?.storePaymentMethod && (
                <Typography variant="body1">
                  {t('paymentForm.storePaymentMethod.msg', { locationName: locationDisplayName })}
                </Typography>
              )}
              <Spacer3 />

              {payWithCardEnabled && (
                <>
                  {isPaymentFormLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      {errorMsg && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {errorMsg}
                        </Alert>
                      )}
                      <AdyenCardPayment
                        sessionId={
                          adyenPaymentToUseFromPendingOrder(bookingCtx.state.data.reservation?.order)?.paymentIntentId
                        }
                        sessionData={bookingCtx.state.data.reservation?.clientSecret}
                        amount={removeDecimal(Math.max(0, depositDue - (isWalletFlowActive ? appliedWalletAmount : 0)))}
                        billingAddress={getBillingAddress(bookingCtx.state.cart.contact!)}
                        currency="USD"
                        paymentFormId="paymentFormId"
                        storeDetailsMsg={`Save card and share with ${locationDisplayName} for future payments`}
                        onPaymentCompleted={onPaymentCompleted}
                        onError={onPaymentError}
                      />
                    </>
                  )}
                </>
              )}
            </Paper>
          )}

          <Stack spacing={3} sx={{ flex: '1 0 49%' }}>
            {bookingConfig.data && order && <SurchargePolicy order={order} bookingConfig={bookingConfig.data} />}
            {!isPrepaid && (
              <Box>
                {isGrooming && (
                  <Alert severity="info">
                    <AlertTitle>{t('paymentForm.groomRequestRefund')}</AlertTitle>
                    {t('paymentForm.groomRequestNotice')}
                  </Alert>
                )}
                {policyCardsData.map((group, index) => (
                  <Fragment key={index}>
                    <FlexCardDisplay cardData={group} />
                    <Spacer height={2} />
                  </Fragment>
                ))}
              </Box>
            )}
          </Stack>
        </Stack>

        <Stack direction="column" spacing={3} flex={1} mt={isMobile ? 3 : 0} width="100%">
          {receipt.isLoading ? (
            <PurchaseReceiptSkeleton />
          ) : (
            <PurchaseReceipt
              receipt={receipt.data}
              dueToday={payingAtFacility ? null : depositDue}
              isVisit={isVisit}
              order={order}
            />
          )}
        </Stack>
      </Stack>

      <Spacer height={STICKY_FOOTER_HEIGHT} />
      <BackContinue
        continueLabel={continueLabel}
        onContinue={onClickContinue}
        continueEnabled={(payingAtFacility || walletCoversDeposit || payWithCardEnabled) && !isPaymentFormLoading}
      />
    </Box>
  )
}
